import { X, Printer } from "lucide-react";
import { formatCurrency, formatDateShort } from "../../types/booking";

const Invoice = ({ booking, onClose, type = "invoice" }) => {
  const isInvoice = type === "invoice";
  const isFullyPaid = (booking.paidAmount || 0) >= (booking.totalAmount || 0);

  const handlePrint = () => {
    window.print();
  };

  const invoiceDate = booking.createdAt?.toDate
    ? booking.createdAt.toDate()
    : new Date(booking.createdAt);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-white text-gray-900 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header - Hidden when printing */}
        <div className="sticky top-0 flex items-center justify-between p-4 bg-white border-b print:hidden">
          <h2 className="text-xl font-bold">
            {isInvoice ? "Invoice" : "Bukti Pelunasan"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-2 transition-colors rounded-lg hover:bg-gray-100"
              title="Print"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 transition-colors rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="p-8" id="invoice-content">
          {/* Header */}
          <div className="pb-6 mb-8 border-b-2 border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <img
                  src="/images/logo-invoice.png"
                  alt="Putri Henna Logo"
                  className="w-auto -ml-5 h-28"
                  onError={(e) => {
                    e.target.style.filter =
                      "brightness(0) sepia(1) saturate(1.5) hue-rotate(330deg) brightness(0.9)";
                  }}
                />
                <div>
                  <h1 className="text-3xl font-bold text-[#D4AF7A] mb-1">
                    Putri Henna
                  </h1>
                  <p className="text-xs text-gray-600">
                    Professional Henna Artistry
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    Jasa Henna Profesional untuk Segala Acara
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="mb-2">
                  <span className="text-xs tracking-wide text-gray-500 uppercase">
                    {isInvoice ? "INVOICE" : "BUKTI PELUNASAN"}
                  </span>
                  <h2 className="text-2xl font-bold">
                    {isInvoice
                      ? booking.bookingNumber
                      : `BP-${booking.bookingNumber}`}
                  </h2>
                </div>
                <p className="text-sm text-gray-600">
                  Tanggal: {formatDateShort(invoiceDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Client & Event Info */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                Informasi Klien
              </h3>
              <div className="space-y-1">
                <p className="font-semibold">{booking.clientName}</p>
                <p className="text-sm text-gray-600">{booking.clientPhone}</p>
                {booking.clientInstagram && (
                  <p className="text-sm text-gray-600">
                    {booking.clientInstagram}
                  </p>
                )}
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                Informasi Acara
              </h3>
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-500">Tanggal:</span>{" "}
                  {formatDateShort(booking.eventDate)}
                </p>
                <p className="text-sm">
                  <span className="text-gray-500">Waktu:</span>{" "}
                  {booking.eventTime}
                </p>
                <p className="text-sm">
                  <span className="text-gray-500">Lokasi:</span>{" "}
                  {booking.eventLocation || "-"}
                </p>
                <p className="text-sm">
                  <span className="text-gray-500">Tipe:</span>{" "}
                  {booking.eventType}
                </p>
              </div>
            </div>
          </div>

          {/* Package Details */}
          <div className="mb-8">
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Detail Paket
            </h3>
            <div className="overflow-hidden border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 uppercase">
                      Paket
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 uppercase">
                      Kategori
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-right text-gray-600 uppercase">
                      Harga
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {booking.items?.map((item, idx) => {
                    const price =
                      item.price || item.packageSnapshot?.price || 0;
                    return (
                      <tr key={idx}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">
                            {item.packageSnapshot?.name || "-"}
                          </p>
                          {item.packageSnapshot?.subcategoryName && (
                            <p className="text-xs text-gray-500">
                              {item.packageSnapshot.subcategoryName}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.packageSnapshot?.categoryName || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-right">
                          {formatCurrency(price)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td
                      colSpan="2"
                      className="px-4 py-3 font-semibold text-right"
                    >
                      Total
                    </td>
                    <td className="px-4 py-3 text-lg font-bold text-right">
                      {formatCurrency(
                        booking.totalAmount ||
                          booking.items?.reduce(
                            (sum, item) =>
                              sum +
                              (item.price || item.packageSnapshot?.price || 0),
                            0,
                          ) ||
                          0,
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="mb-8">
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Ringkasan Pembayaran
            </h3>
            <div className="p-4 space-y-2 rounded-lg bg-gray-50">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Tagihan</span>
                <span className="font-medium">
                  {formatCurrency(booking.totalAmount || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Deposit ({booking.depositPercent || 30}%)
                </span>
                <span className="font-medium">
                  {formatCurrency(booking.depositAmount || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sisa Pembayaran</span>
                <span className="font-medium">
                  {formatCurrency(booking.remainingAmount || 0)}
                </span>
              </div>
              <div className="flex justify-between pt-2 text-base font-bold border-t">
                <span>Total Dibayar</span>
                <span className="text-green-600">
                  {formatCurrency(booking.paidAmount || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment History (for Invoice) */}
          {isInvoice && booking.payments && booking.payments.length > 0 && (
            <div className="mb-8">
              <h3 className="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                Riwayat Pembayaran
              </h3>
              <div className="overflow-hidden border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-xs font-semibold text-left text-gray-600 uppercase">
                        Tanggal
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-left text-gray-600 uppercase">
                        Metode
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-right text-gray-600 uppercase">
                        Jumlah
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-left text-gray-600 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {booking.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-4 py-2">
                          {formatDateShort(payment.createdAt)}
                        </td>
                        <td className="px-4 py-2">{payment.method}</td>
                        <td className="px-4 py-2 font-medium text-right">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              payment.status === "VERIFIED"
                                ? "bg-green-100 text-green-700"
                                : payment.status === "PENDING"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700"
                            }`}
                          >
                            {payment.status === "VERIFIED"
                              ? "Terverifikasi"
                              : payment.status === "PENDING"
                                ? "Menunggu"
                                : "Ditolak"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment Status Badge */}
          <div className="text-center">
            {isFullyPaid ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 border-2 border-green-500 rounded-lg bg-green-50">
                <svg
                  className="w-6 h-6 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-lg font-bold text-green-700">LUNAS</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-6 py-3 border-2 border-yellow-500 rounded-lg bg-yellow-50">
                <svg
                  className="w-6 h-6 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-lg font-bold text-yellow-700">
                  BELUM LUNAS
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-6 mt-8 text-sm text-center text-gray-500 border-t">
            <p>Terima kasih atas kepercayaan Anda!</p>
            <p className="mt-1">Hubungi kami untuk pertanyaan lebih lanjut.</p>
            <p className="mt-2 font-semibold text-[#D4AF7A]">
              Putri Henna - Professional Henna Artistry
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-content,
          #invoice-content * {
            visibility: visible;
          }
          #invoice-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 2cm;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;
